import {
  ccDescriptionColumnDefinition,
  companyCodeColumnDefinition,
  countryNameColumnDefinition,
  countryTaxTypeColumnDefinition,
} from '../../shared/columnDefinitions';
import { DecimalRenderer } from '../../shared/numeralRenderers';

import routes from '../../routes';

import PercentageRenderer from '../../shared/cellRenderers/percentageCellRenderer.component';
import { CellExportFormats } from '@common/xlsxExport/xlsxExport.constants';
import { toSearchString } from '../../shared/urlUtils';
import { compareScenariosPrefixes } from '../shared/constants';
import linkCellRenderer from './LinkCellRenderer.component';

const columnPivotOptions = {
  filter: 'agSetColumnFilter',
  editable: true,
  enablePivot: true,
  enableRowGroup: true,
  aggFunc: 'sum',
};

const getReportLink = (scenarioId, row) => {
  const companyCode = row.node.group
    ? row.node.aggData.COMPANY_CODE
    : row.data.COMPANY_CODE;

  const countryTaxType = row.node.group
    ? row.node.aggData.COUNTRY_TAX_TYPE
    : row.data.COUNTRY_TAX_TYPE;

  const groupField = row.node.group ? row.node.field : null;

  const queryString = toSearchString({
    companyCodes: companyCode?.split(','),
    countryTaxTypes: countryTaxType?.split(','),
    groupField,
  });

  const endpoint = routes.scenarios.PROVISION_REPORT
    .replace(/:scenarioId/, scenarioId);

  return companyCode ? `${endpoint}?${queryString}` : '';
};

const varianceColumnFactory = ({ baseColumn, compareColumn }) => ({
  headerName: 'Variance',
  valueGetter: ({ node, data }) => {
    if (!node.group) {
      return data[baseColumn.field] - data[compareColumn.field];
    }
    return node.aggData[baseColumn.colId] - node.aggData[compareColumn.colId];
  },
  cellRenderer: baseColumn.exportFormat === CellExportFormats.PERCENT_4
    ? PercentageRenderer
    : DecimalRenderer,
});

const compareColumnFactory = ({ baseScenarioId, columnFactory }) => {
  const baseColumn = columnFactory({
    baseScenarioId,
    prefix: compareScenariosPrefixes.base,
  });

  const compareColumn = columnFactory({
    baseScenarioId,
    prefix: compareScenariosPrefixes.compare,
  });

  const { headerName } = baseColumn;
  baseColumn.headerName = '1st scenario';
  compareColumn.headerName = '2nd scenario';

  return {
    headerName,
    children: [
      baseColumn,
      compareColumn,
      varianceColumnFactory({ baseColumn, compareColumn }),
    ],
  };
};

const etrColumnFactory = ({ baseScenarioId, prefix = compareScenariosPrefixes.empty }) => ({
  headerName: 'ETR',
  field: `${prefix}ETR`,
  colId: `${prefix}ETR`,
  ...columnPivotOptions,
  aggFunc: null,
  cellRenderer: linkCellRenderer(PercentageRenderer),
  cellRendererParams: row => ({
    link: getReportLink(baseScenarioId, row),
  }),
  varianceCellRenderer: PercentageRenderer,
});

const totalExpenseColumnFactory = ({ baseScenarioId, prefix = compareScenariosPrefixes.empty }) => ({
  headerName: 'Total Tax Expense',
  field: `${prefix}TOTAL_TAX_EXPENSE`,
  colId: `${prefix}TOTAL_TAX_EXPENSE`,
  ...columnPivotOptions,
  cellRenderer: linkCellRenderer(DecimalRenderer),
  cellRendererParams: row => ({
    link: getReportLink(baseScenarioId, row),
  }),
});


const getFunctionalCurrencyColumn = ({ compareScenarioId, hasDifferentCurrencies }) => {
  if (!compareScenarioId) {
    return {
      headerName: 'Functional currency',
      field: 'FUNCTIONAL_CURRENCY',
      ...columnPivotOptions,
      aggFunc: 'uniqList',
    };
  }

  if (!hasDifferentCurrencies) {
    return {
      headerName: 'Functional currency',
      field: 'BASE_FUNCTIONAL_CURRENCY',
      aggFunc: 'uniqList',
    };
  }

  return {
    headerName: 'Functional currency',
    children: [
      {
        headerName: '1st scenario',
        field: 'BASE_FUNCTIONAL_CURRENCY',
        aggFunc: 'uniqList',
      },
      {
        headerName: '2nd scenario ',
        field: 'COMPARE_FUNCTIONAL_CURRENCY',
        aggFunc: 'uniqList',
      },
    ],
  };
};


const getColumnDefinitions = ({
  scenarioId,
  compareScenarioId,
  hasDifferentCurrencies,
}) => [
  {
    ...companyCodeColumnDefinition,
    field: 'COMPANY_CODE',
    colId: 'COMPANY_CODE',
    ...columnPivotOptions,
    rowGroup: true,
    aggFunc: 'uniqList',
  },
  {
    ...ccDescriptionColumnDefinition,
    field: 'CC_DESCRIPTION',
    aggFunc: 'uniqList',
  },
  {
    ...countryNameColumnDefinition,
    ...columnPivotOptions,
    headerName: 'Country',
    field: 'COUNTRY_NAME',
    colId: 'COUNTRY_NAME',
    aggFunc: 'uniqList',
  },
  {
    ...countryTaxTypeColumnDefinition,
    ...columnPivotOptions,
    headerName: 'Jurisdiction',
    field: 'COUNTRY_TAX_TYPE',
    colId: 'COUNTRY_TAX_TYPE',
    aggFunc: 'uniqList',
  },

  getFunctionalCurrencyColumn({ compareScenarioId, hasDifferentCurrencies }),
  ...[
    etrColumnFactory,
    totalExpenseColumnFactory,
  ].map(columnFactory => compareScenarioId
    ? compareColumnFactory({ baseScenarioId: scenarioId, columnFactory })
    : columnFactory({ baseScenarioId: scenarioId })
  ),
];


export default getColumnDefinitions;
